import {createAsyncThunk, createSlice} from '@reduxjs/toolkit';
import {RootState} from "../../../app/store";
import {globalFetchConfig, RequestStatus} from "../../../api/apiConfiguration";
import {SelectOption} from "../../elements/select/SelectDropdown";
import {BACKEND_URL} from "../../../configs";
import {GetAvailableNewVehicleManufacturersVehicleTypeEnum} from "../../../api/generated";

export const fetchNewVehicleManufacturerOptions = createAsyncThunk<Array<string>, GetAvailableNewVehicleManufacturersVehicleTypeEnum | undefined>(
    "newVehicleManufacturerOptions/fetch",
    async (vehicleType) => {
        const query = vehicleType ? `?vehicleType=${vehicleType}` : "";
        const response = await globalFetchConfig(`${BACKEND_URL}/api/private/franchise/new-vehicle/manufacturers${query}`, {
            method: "GET"
        });
        if (!response.ok) {
            throw new Error("Failed to fetch new vehicle manufacturers");
        }
        return response.json();
    }
);

export interface NewVehicleManufacturerOptionsState {
    manufacturerOptions: SelectOption<string>[];
    requestStatus: RequestStatus;
}

const buildInitialState = (): NewVehicleManufacturerOptionsState => ({
    manufacturerOptions: [],
    requestStatus: RequestStatus.NONE
});

const initialState = buildInitialState();

export const newVehicleManufacturerOptionsSlice = createSlice({
    name: "newVehicleManufacturerOptions",
    initialState,
    reducers: {
        resetNewVehicleManufacturerOptionsState: (state) => {
            state.manufacturerOptions = [];
            state.requestStatus = RequestStatus.NONE;
        }
    },
    extraReducers: (builder) => {
        builder.addCase(fetchNewVehicleManufacturerOptions.pending, (state) => {
            state.requestStatus = RequestStatus.PENDING;
        });
        builder.addCase(fetchNewVehicleManufacturerOptions.fulfilled, (state, {payload}) => {
            state.manufacturerOptions = payload
                .map((manufacturer) => ({label: manufacturer, value: manufacturer}));
            state.requestStatus = RequestStatus.RESOLVED;
        });
        builder.addCase(fetchNewVehicleManufacturerOptions.rejected, (state) => {
            state.requestStatus = RequestStatus.REJECTED;
        });
    }
});

export const {resetNewVehicleManufacturerOptionsState} = newVehicleManufacturerOptionsSlice.actions;

export const newVehicleManufacturerOptionsSelector = (state: RootState): SelectOption<string>[] =>
    state.newVehicleManufacturerOptions.manufacturerOptions;

export const newVehicleManufacturerOptionsRequestStatusSelector = (state: RootState): RequestStatus =>
    state.newVehicleManufacturerOptions.requestStatus;
